// @ts-strict-ignore
import {
  getLocalizedIndex,
  getParentRule,
  isAmbiguousGroup,
  isFirstChild,
  isGroupRule,
  isThresholdRule,
  isTreeRoot,
} from 'flow/service/rules';
import type { GroupRule, RuleBuilderRule } from 'flow/service/rules';
import type { GroupPosition } from '../../types';
import type { Action } from 'flow/store/engine/types';
import { isActionOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';

interface GetHasPendingParenthesisProps {
  rule: RuleBuilderRule;
  ruleTree: RuleBuilderRule;
  pendingGroupInfo: {
    parentRuleId: RuleBuilderRule['id'] | null;
    startIndex: number | null;
  };
  /**
   * The relative position within a group when rendering a line using RuleTemplate.
   * When at a real group start line (groupPosition === 'start'), we should not
   * render a pending close parenthesis for an already-opened group because the
   * line is out of scope for closing. We still allow closing on the same line
   * as a pending open, which only occurs on threshold lines, not group starts.
   */
  groupPosition?: GroupPosition;
}

function getHasPendingParenthesis(args: GetHasPendingParenthesisProps & {
  parenthesisType: 'open' | 'close';
}) {
  const {
    parenthesisType, rule, ruleTree, pendingGroupInfo, groupPosition,
  } = args;
  const { parentRuleId, startIndex } = pendingGroupInfo;

  // no group has been opened
  if (startIndex === null || isTreeRoot(rule)) {
    return false;
  }

  const parentRule = getParentRule(ruleTree, rule);

  // the opened group and this rule do not share the same parent
  if (parentRule.id !== parentRuleId) {
    return false;
  }

  const localIndex = getLocalizedIndex(rule.data.path);

  // open parenthesis should be rendered *only* on the specified startIndex
  if (parenthesisType === 'open') {
    return startIndex === localIndex;
  }

  // For closing parenthesis: render on and after startIndex, except when this
  // line is the real start line of another group. In that case we suppress the
  // close button as it's out of scope for closing the previously opened group.
  const isRealGroupStartLine = isGroupRule(rule) && groupPosition === 'start';
  if (isRealGroupStartLine) {
    return false;
  }

  return startIndex <= localIndex;
}

/** Returns whether a line should render an open parenthesis */
export function getHasPendingOpenParenthesis(args: GetHasPendingParenthesisProps) {
  return getHasPendingParenthesis({ ...args, parenthesisType: 'open' });
}

/** Returns whether a line should render a closing parenthesis */
export function getHasPendingCloseParenthesis(args: GetHasPendingParenthesisProps) {
  return getHasPendingParenthesis({ ...args, parenthesisType: 'close' });
}

/** Generic instance/index matcher for UI state records */
export function isInstanceIndexMatch(
  record: { instanceId: string; index: number } | null,
  instanceId: string,
  index: number,
) {
  return Boolean(record)
    && record.instanceId === instanceId
    && record.index === index;
}

type GroupSelectType = 'parent' | 'grandparent';

/**
 * Determines what level of nesting relative to the current rule
 * to render the grouptype toggle for.
 */
function getGroupSelectType(args: {
  rule: RuleBuilderRule;
  ruleTree: RuleBuilderRule;
  groupPosition?: GroupPosition;
}): GroupSelectType | null {
  const { rule, ruleTree, groupPosition } = args;
  const parentRule = getParentRule(ruleTree, rule);

  if (!parentRule) {
    return null;
  }

  // first child of an ambiguous group renders the grandparent rule grouptype
  if (isAmbiguousGroup(parentRule) && isFirstChild(rule) && groupPosition !== 'end') {
    return 'grandparent';
  }

  const hasParent = parentRule && isGroupRule(parentRule);
  const hasParentSelect = (
    hasParent
    && groupPosition !== 'end'
    && !isFirstChild(rule)
  );

  if (hasParentSelect) {
    return 'parent';
  }

  // render no grouptype toggle
  return null;
}

/** Returns the group to render into the GroupType toggle */
export function getGroupSelectRule(args: {
  rule: RuleBuilderRule;
  ruleTree: RuleBuilderRule;
  groupPosition?: GroupPosition;
}): GroupRule | null {
  const { rule, ruleTree } = args;
  const groupSelectType = getGroupSelectType(args);
  switch (groupSelectType) {
    case 'grandparent': {
      const parentRule = getParentRule(ruleTree, rule);
      return getParentRule(ruleTree, parentRule);
    }
    case 'parent': {
      return getParentRule(ruleTree, rule);
    }
    default: {
      return null;
    }
  }
}

export function getCanDeleteRule(
  sourceAction: Action,
  rule: RuleBuilderRule,
  hasMultipleThresholds: boolean,
) {
  if (!isThresholdRule(rule)) return false;

  if (hasMultipleThresholds) return true;

  const supportsNoRules = isActionOfType(
    sourceAction,
    [ActionType.MATRIX_MODEL, ActionType.SERVICE, ActionType.SNIPPET],
  );

  return supportsNoRules;
}

